import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import useNavigateWithBase from "../hooks/useNavigateWithBase"; // ✅ 新しいフックをインポート
import DOMPurify from 'dompurify'; // ✅ HTMLインジェクション対策
import "../CSS/DocumentsPage.css";  // ← 追加

const DocumentsPage = () => {
  const { state } = useLocation();
  const navigate = useNavigateWithBase();
  
  const [companyName, setCompanyName] = useState("");
  const [personalName, setPersonalName] = useState("");
  const [projectId, setProjectId] = useState("");
  const [documentType, setDocumentType] = useState("");
  const [file, setFile] = useState(null);
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [page, setPage] = useState(1); // ✅ ページ番号のステート
  const itemsPerPage = 20; // 🔥 1ページに表示するアイテム数を設定

  // ✅ 現在のページに応じたデータの取得範囲
  const [allDocuments, setAllDocuments] = useState([]); // 🔥 すべてのドキュメント
  const startIndex = Math.max(0, (page - 1) * itemsPerPage);
  const endIndex = Math.min(startIndex + itemsPerPage, allDocuments.length);
  const allowedExtensions = ['.docx', '.doc', '.xlsx', '.xls', '.pptx', '.ppt', '.pdf', '.txt'];
  const allowedMimeTypes = [
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.ms-powerpoint',
    'application/pdf',
    'text/plain'
  ];
  const totalPages = Math.ceil(totalDocuments / itemsPerPage);
  console.log(`📌 DEBUG: 総ページ数: ${totalPages}, 現在のページ: ${page}`);

  useEffect(() => {
    if (state) {
      setCompanyName(state.companyName || "");
      setPersonalName(state.personalName || "");
      setProjectId(state.projectId || "");
    }
  }, [state]);

  const currentPageDocuments = useMemo(() => {
    console.log("📌 DEBUG: allDocuments = ", allDocuments);
    console.log("📌 DEBUG: startIndex = ", startIndex, "endIndex = ", endIndex);

    if (!Array.isArray(allDocuments) || allDocuments.length === 0) {
        return []; // ✅ データがない場合は空配列を返す
    }

    return allDocuments;
  }, [allDocuments]);


  // ✅ 入力値のサニタイズ（HTMLインジェクション防止）
  const sanitizeInput = (input) => {
    return DOMPurify.sanitize(input);
  };

  const handleFileChange = (event) => {
    setFile(event.target.files[0]);
  };

  const handleFileUpload = async () => {
    if (!file || !documentType || !companyName || !projectId) {
      alert("全ての情報を入力してください");
      return;
    }

    const fileExtension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
    if (!allowedExtensions.includes(fileExtension) || !allowedMimeTypes.includes(file.type)) {
      alert("許可されたファイル形式（ワード・エクセル・パワポ・テキスト・PDF）のみアップロードできます。");
      return;
    }
  
    const doc_Type = sanitizeInput(documentType);

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = async () => {
      const base64File = reader.result.split(',')[1]; // Base64 部分のみ取得
  
      const requestBody = {
        action: "upload",
        documentType: doc_Type,
        companyName,
        projectId,
        fileName: file.name,
        fileContent: base64File,
        fileMimeType: file.type,
      };
  
      try {
        await axios.post("https://qvekqcbobc.execute-api.ap-northeast-3.amazonaws.com/prod/api/documents", requestBody);
        alert("ファイルアップロード成功");
        fetchDocuments();
        setFile(null);
        document.getElementById("file-input").value = "";
      } catch (error) {
        alert("アップロード失敗");
      }
    };
  };  

  const fetchDocuments = useCallback(async () => {
    try {
        console.log(`📌 DEBUG: Fetching documents for page = ${page}`);
        const response = await axios.post(
            "https://qvekqcbobc.execute-api.ap-northeast-3.amazonaws.com/prod/api/documents",
            { action: "fetch", projectId, page, itemsPerPage }
        );

        console.log("📌 DEBUG: API Response", response.data);

        if (response.data && Array.isArray(response.data.documents)) {
            setAllDocuments(response.data.documents);
            setTotalDocuments(response.data.totalDocuments);
        } else {
            console.error("❌ [ERROR] APIのレスポンスが不正:", response.data);
        }
    } catch (error) {
        console.error("❌ [ERROR] 資料の取得に失敗:", error);
        alert("資料の取得に失敗しました");
    }
  }, [projectId, page]);

  useEffect(() => {
      if (projectId) {
          fetchDocuments();
      }
  }, [projectId, page]); // ✅ `page` が変更されたらデータを再取得


  const handleDownload = async (documentId, filename) => {
    console.log("ダウンロードリクエスト:", documentId, filename); // ★ 確認用ログ
    
    if (!documentId) {
      alert("ダウンロードするファイルが見つかりません");
      return;
    }
  
    try {
      const response = await axios.post("https://qvekqcbobc.execute-api.ap-northeast-3.amazonaws.com/prod/api/documents", {
        action: "getSignedUrl",
        documentId,
      });
  
      console.log("サーバーからのレスポンス:", response.data); // ★ 確認用ログ
  
      if (response.data.signedUrl) {
        // 署名付きURLからファイルを取得
        const fileResponse = await fetch(response.data.signedUrl);
        const blob = await fileResponse.blob();
  
        // ファイルをダウンロード
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.setAttribute("download", filename); // ✅ ファイル名を指定
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        alert("ダウンロードURLの取得に失敗しました");
      }
    } catch (error) {
      console.error("ダウンロードエラー:", error.response?.data || error.message);
      alert("ダウンロード処理に失敗しました");
    }
  };  

  const handleDelete = async (documentId) => {
    if (!window.confirm("この資料を削除してもよろしいですか？")) {
      return;
    }

    try {
      await axios.post("https://qvekqcbobc.execute-api.ap-northeast-3.amazonaws.com/prod/api/documents", { action: "delete", documentId });
      alert("資料を削除しました");
      fetchDocuments();
    } catch (error) {
      alert("削除失敗");
    }
  };

  const handleNextPage = () => {
    const totalPages = Math.ceil(totalDocuments / itemsPerPage);

    console.log(`📌 DEBUG: 次のページチェック - 現在のページ: ${page}, 総ページ数: ${totalPages}`);

    if (page < totalPages) {
        setPage(prevPage => {
            console.log(`📌 DEBUG: ページを ${prevPage + 1} に更新`);
            return prevPage + 1;
        });
    }
  };

  const handlePrevPage = () => {
      setPage(prevPage => (prevPage > 1 ? prevPage - 1 : 1));
  };

  const formatDate = (dateString) => {
    if (!dateString) return "不明";
    const date = new Date(dateString);
    return `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, "0")}/${String(date.getDate()).padStart(2, "0")}`;
  };


  return (
    <div className="documents-container">
      <h1>プロジェクト資料一覧</h1>

      <div className="user-info">
        <p>会社名: {companyName}</p>
        <p>ユーザ名: {personalName}</p>
        <p>プロジェクトID: {projectId}</p>
      </div>

      <div className="upload-form">
        <div className="upload-data">
            <input
                type="text"
                placeholder="資料種別"
                value={documentType}
                onChange={(e) => setDocumentType(e.target.value)}
            />
            <input
                type="file"
                id="file-input"
                onChange={handleFileChange}
            />
        </div>
        <button type="button" onClick={handleFileUpload} className="documents-upload">
          アップロード
        </button>
      </div>
      <table className="data-table">
      <thead>
          <tr>
            <th>ドキュメントID</th><th>作成日</th><th>資料種別</th><th>資料名</th><th>操作</th>
          </tr>
        </thead>
        <tbody>
          {currentPageDocuments.length > 0 ? (
            currentPageDocuments.map((doc) => {
              const cleanFileName = doc.filename.replace(/^[A-Za-z0-9]{4}-[A-Za-z0-9]{4}-[A-Za-z0-9]{4}-/, '');
              return (
                <tr key={doc.id}>
                  <td>{doc.id}</td>
                  <td>{formatDate(doc.created_at)}</td> {/* ✅ 追加 */}
                  <td>{doc.document_type}</td>
                  <td>{cleanFileName}</td>
                  <td className="control-panel">
                    <button className="download-button" onClick={() => handleDownload(doc.id, cleanFileName)}>ダウンロード</button>
                    <button className="delete-button" onClick={() => handleDelete(doc.id)}>削除</button>
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan="5" style={{ textAlign: "center" }}>データがありません</td> {/* ✅ colSpan 修正 */}
            </tr>
          )}
        </tbody>
      </table>
      <div className="pagination">
        <button onClick={handlePrevPage} disabled={page === 1}>前のページ</button>
        <span className='page'>{page} / {totalPages}</span>
        <button onClick={handleNextPage} disabled={page >= totalPages}>次のページ</button>
      </div>
      <button type="button" className="back-button" onClick={() => navigate('/portal')}>
        ポータルへ戻る
      </button>
    </div>
  );
};

export default DocumentsPage;
